import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import Layout from '../../components/Layout'
import { apiError } from '../../api/account'
import { getShareLink, sendRecipe } from '../../api/recipeRequests'

import '../PurchaseBook/styles.css'
import styles from './ShareRecipe.module.css'

// The page a relative opens from a recipe request link: no account needed. Name and email
// are required; the Morsel emails box is separate and unticked.
const ShareRecipe = () => {
  const { token } = useParams()
  const [page, setPage] = useState(null)
  const [missing, setMissing] = useState('')
  const [form, setForm] = useState({
    contributor_name: '',
    contributor_email: '',
    recipe_name: '',
    ingredients_text: '',
    steps_text: '',
    notes: '',
    website: '', // hidden: people never fill it in
  })
  const [consent, setConsent] = useState(false)
  const [photos, setPhotos] = useState([])
  const [fields, setFields] = useState({})
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(null)
  const startedAt = useRef(Date.now())

  useEffect(() => {
    getShareLink(token)
      .then(({ data }) => {
        setPage(data)
        setForm((f) => ({ ...f, recipe_name: data.recipe_name || '' }))
      })
      .catch((e) =>
        setMissing(apiError(e, 'This link is not working any more. Ask for a new one.'))
      )
  }, [token])

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value })
  const max = page?.max_photos || 3

  const addPhotos = (e) => {
    const chosen = Array.from(e.target.files || [])
    e.target.value = ''
    setPhotos([...photos, ...chosen].slice(0, max))
  }

  const send = (e) => {
    e.preventDefault()
    setError('')
    setFields({})
    if (!form.ingredients_text.trim() && !form.steps_text.trim() && !photos.length) {
      return setFields({ recipe: 'Type the recipe or add a photo of the card' })
    }
    const data = new FormData()
    Object.entries(form).forEach(([k, v]) => data.append(k, v))
    data.append('consent_marketing', consent ? 'true' : 'false')
    data.append('started_at', String(startedAt.current))
    photos.forEach((p, i) => data.append('photos[]', p, p.name || `card-${i + 1}.jpg`))
    setSending(true)
    sendRecipe(token, data)
      .then(({ data: result }) => setSent(result))
      .catch((err) => {
        setFields(err?.response?.data?.fields || {})
        setError(apiError(err, 'Something went wrong. Please try again.'))
      })
      .finally(() => setSending(false))
  }

  let body
  if (missing) body = <p className="cbError">{missing}</p>
  else if (!page) body = <p className="cbMuted">Loading…</p>
  else if (sent) {
    body = (
      <section className="cbSection">
        <h2 className={styles.big}>Thank you!</h2>
        <p>
          We sent your recipe to {sent.requester_name}. It joins {page.group_name}’s recipes on
          Morsel once they add it.
        </p>
        <p className="cbMuted">You can close this page.</p>
      </section>
    )
  } else {
    const field = (key, label, input, hint) => (
      <label className={styles.field}>
        {label}
        {hint ? <span className="cbMuted">{hint}</span> : null}
        {input}
        {fields[key] ? <span className={styles.fieldError}>{fields[key]}</span> : null}
      </label>
    )
    body = (
      <form className={`cbSection ${styles.form}`} onSubmit={send} noValidate>
        {field(
          'contributor_name',
          'Your name',
          <input
            className={`cbInput ${fields.contributor_name ? 'cbInputBad' : ''}`}
            value={form.contributor_name}
            onChange={set('contributor_name')}
            autoComplete="name"
            maxLength={100}
            required
          />
        )}
        {field(
          'contributor_email',
          'Your email',
          <input
            type="email"
            inputMode="email"
            className={`cbInput ${fields.contributor_email ? 'cbInputBad' : ''}`}
            value={form.contributor_email}
            onChange={set('contributor_email')}
            autoComplete="email"
            maxLength={254}
            required
          />,
          'So the family knows it’s from you.'
        )}
        {field(
          'recipe_name',
          'Recipe name',
          <input
            className={`cbInput ${fields.recipe_name ? 'cbInputBad' : ''}`}
            value={form.recipe_name}
            onChange={set('recipe_name')}
            maxLength={100}
            required
          />
        )}

        <div className={styles.photoBox}>
          <p className="cbLabel">Snap the recipe card</p>
          <p className="cbMuted">Up to {max} photos, front and back. Or type it below.</p>
          <div className={styles.photoRow}>
            {photos.map((p, i) => (
              <span key={`${p.name}-${i}`} className={styles.photoChip}>
                Photo {i + 1}
                <button
                  type="button"
                  aria-label={`Remove photo ${i + 1}`}
                  onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
            {photos.length < max ? (
              <label className={`cbSecondaryButton ${styles.photoButton}`}>
                {photos.length ? 'Add another photo' : 'Take or choose a photo'}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={addPhotos}
                />
              </label>
            ) : null}
          </div>
        </div>

        {field(
          'ingredients_text',
          'Ingredients',
          <textarea
            className={`cbInput ${styles.area}`}
            value={form.ingredients_text}
            onChange={set('ingredients_text')}
            rows={5}
            maxLength={8000}
            placeholder={'1 cup pecans\n3 eggs'}
          />,
          'One per line.'
        )}
        {field(
          'steps_text',
          'How to make it',
          <textarea
            className={`cbInput ${styles.area}`}
            value={form.steps_text}
            onChange={set('steps_text')}
            rows={6}
            maxLength={16000}
            placeholder={'Mix everything together.\nBake at 350 for 55 minutes.'}
          />,
          'One step per line.'
        )}
        {field(
          'notes',
          'Anything else? (optional)',
          <textarea
            className={`cbInput ${styles.area}`}
            value={form.notes}
            onChange={set('notes')}
            rows={3}
            maxLength={2000}
            placeholder="Grandma always used dark corn syrup."
          />
        )}
        {fields.recipe ? <p className="cbError">{fields.recipe}</p> : null}

        <input
          className={styles.hidden}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          name="website"
          value={form.website}
          onChange={set('website')}
        />

        <label className={styles.consent}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>{page.consent_text}</span>
        </label>

        {error && !Object.keys(fields).length ? <p className="cbError">{error}</p> : null}
        <button type="submit" className={`cbNextButton ${styles.send}`} disabled={sending}>
          {sending ? 'Sending…' : `Send to ${page.requester_name}`}
        </button>
        <p className="cbMuted">
          No account needed. {page.requester_name} sees your name, email and the recipe.
        </p>
      </form>
    )
  }

  return (
    <Layout title="Share a family recipe" allowGuests>
      <div className={`cbPage ${styles.page}`}>
        <div className="cbIntro">
          <div>
            <h1 className="cbTitle">
              {page ? `${page.requester_name} asked for a recipe` : 'Share a family recipe'}
            </h1>
            {page ? (
              <p className="cbSubtitle">
                {page.recipe_name ? `“${page.recipe_name}” for ` : 'For '}
                {page.group_name} on Morsel.
              </p>
            ) : null}
          </div>
        </div>
        {body}
      </div>
    </Layout>
  )
}

export default ShareRecipe
